import React, { useEffect, useState } from 'react';
import { Select, Typography, Alert, Tag } from 'antd';
import { useAspSelector } from '../../../../../../test/jest-redux-hooks';
import { inboxTeamMembers } from '../../../../../inbox/api/Api';

const { Text } = Typography;
const { Option } = Select;

const Assignee = ({ data, onChange, node }) => {
  const { currentBrand } = useAspSelector((state) => state.app);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch team members when component mounts
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await inboxTeamMembers(currentBrand?.brand_id, null, true);
        if (response?.data?.users) {
          // Create a map to store unique users by id
          const uniqueUsersMap = new Map();
          
          response.data.users.forEach(user => {
            // Use id as the key to ensure uniqueness
            if (!uniqueUsersMap.has(user.id)) {
              uniqueUsersMap.set(user.id, user);
            }
          });
          
          // Convert map values back to array
          const uniqueUsers = Array.from(uniqueUsersMap.values());
          setTeamMembers(uniqueUsers);
        }
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Failed to load team members. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (currentBrand?.brand_id) {
      fetchTeamMembers();
    }
  }, [currentBrand?.brand_id]);

  const handleAssigneeChange = (values) => {
    // If values is empty, clear the selection
    if (!values || values.length === 0) {
      const payload = {
        key: 'data',
        data: {
          type: node?.type?.toLowerCase(),
          [node?.type?.toLowerCase()]: [],
          operations: { assignee: [] },
        },
      };

      onChange({
        type: 'UPDATE_NODE',
        id: node?.uniq_id,
        ...payload,
      });
      return;
    }

    // Get full member objects for selected values
    const selectedMembers = values.map(value => {
      if (typeof value === 'object') {
        // Handle the case where we have key/value objects
        if (value.key && value.value) {
          return {
            id: value.value,
            key: value.key,
            label: value.label
          };
        }
        return value; // Already have full object
      }
      return teamMembers.find(member => member.id.toString() === value.toString()) || { id: value };
    });
    
    const payload = {
      key: 'data',
      data: {
        type: node?.type?.toLowerCase(),
        [node?.type?.toLowerCase()]: selectedMembers,
        operations: { assignee: selectedMembers },
      },
    };

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });
  };

  const getCurrentAssignees = () => {
    // Only show assignees if we have team members loaded OR if we have full assignee data with labels
    if (data?.operations?.assignee?.length) {
      return data.operations.assignee
        .filter(member => {
          // Only show if we have a proper label or if team members are loaded
          return member.label || teamMembers.length > 0;
        })
        .map(member => {
          // If we already have a label, use it
          if (member.label) {
            return {
              key: member.id.toString(),
              label: member.label,
              value: member.id.toString(),
              ...member
            };
          }
          
          // Otherwise, try to find the member in team members
          const teamMember = teamMembers.find(tm => tm.id.toString() === member.id.toString());
          if (teamMember) {
            return {
              key: member.id.toString(),
              label: `${teamMember.user_name} (${teamMember.email})`,
              value: member.id.toString(),
              ...member
            };
          }
          
          // Don't return anything if we don't have proper data
          return null;
        })
        .filter(Boolean); // Remove null values
    }
    
    return [];
  };

  const getAssigneeName = (assigneeId) => {
    const member = teamMembers.find(member => member.id.toString() === assigneeId.toString());
    return member ? `${member.user_name} (${member.email})` : assigneeId;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose, isMaxTag } = props;
    
    // Handle the overflow tag ("+X ..." tag)
    if (isMaxTag) {
      return (
        <span className="inline-flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-[500] px-3 py-1.5 rounded-full ml-1 border border-blue-200">
          {label}
        </span>
      );
    }
    
    // Don't render anything if the value is empty or undefined
    if (!value || !label) {
      return null;
    }
    
    // Handle regular assignee tags
    const assigneeName = label || getAssigneeName(value);
    const nameInitial = assigneeName ? assigneeName.charAt(0).toUpperCase() : 'U';
    
    return (
      <span className="inline-flex my-[4px] items-center text-[12px] bg-gradient-to-r from-white to-gray-50 border border-gray-200 text-gray-800 text-sm font-[500] px-3 py-1.5 rounded-full hover:from-gray-50 hover:to-gray-100 hover:border-gray-300 transition-all duration-200 mr-2 shadow-sm hover:shadow-md">
        <span className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-[600] mr-2 shadow-sm">
          {nameInitial}
        </span>
        <span className="max-w-[200px] truncate font-[500] text-[12px]">{assigneeName}</span>
        {closable && (
          <button
            type="button"
            onClick={onClose}
            className="ml-2 text-gray-400 hover:text-red-500 focus:outline-none transition-colors duration-200"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </span>
    );
  };

  return (
    <div className="rounded-lg">
      <div className="mb-6">
        <div className="flex items-start space-x-3 mb-4">
          {/* <div className="flex-shrink-0 mt-1">
            <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div> */}
          <div className="flex-1">
            {/* <h3 className="text-lg font-[600] text-gray-900 mb-1">Assign Team Members</h3> */}
            <p className="text-sm text-gray-600 leading-relaxed">
              Assign this conversation to team members who will be responsible for following up.
            </p>
          </div>
        </div>
      </div>
      
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="mb-6 rounded-lg"
          closable
          onClose={() => setError('')}
        />
      )}

      <div className="mb-">
        <label className="block text-sm font-[600] text-gray-700 mb-3">
          Select Team Members
        </label>
        <div className="relative">
          <Select
            mode="multiple"
            className="w-full !ring-0 hover:!ring-0 focus:!ring-0 focus:!ring-offset-0 focus:!border-transparent hover:!border-gray-300"
            style={{
              minHeight: '48px'
            }}
            dropdownMatchWidth={false}
            suffixIcon={null}
            placeholder={
              <div className="flex items-center text-gray-500 py-1">
                <svg className="h-5 w-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="font-[500]">Search and select team members...</span>
              </div>
            }
            value={getCurrentAssignees()}
            onChange={handleAssigneeChange}
            loading={loading}
            showSearch
            optionFilterProp="searchText"
            filterOption={(input, option) => {
              const searchText = option?.searchText?.toLowerCase() || '';
              const inputLower = input.toLowerCase();
              return searchText.includes(inputLower);
            }}
            tagRender={tagRender}
            optionLabelProp="label"
            labelInValue
            maxTagCount="responsive"
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            dropdownClassName="rounded-xl shadow-xl border-0 [&_.ant-select-item-option-selected]:!bg-transparent [&_.ant-select-item-option-active]:!bg-transparent [&_.ant-select-item-option-selected_.ant-select-item-option-state]:hidden"
            dropdownStyle={{
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: 'none',
              overflow: 'hidden'
            }}
            listHeight={300}
            size="large"
            notFoundContent={
              <div className="py-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <p className="text-gray-500 font-[500]">No team members found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
              </div>
            }
          >
            {teamMembers.map((member) => {
              const displayName = `${member.user_name} (${member.email})`;
              const searchText = `${member.user_name} ${member.email}`.toLowerCase();
              
              return (
                <Option 
                  key={member.id} 
                  value={member.id.toString()}
                  label={displayName}
                  searchText={searchText}
                  className="!px-0 !py-0 hover:!bg-transparent [&.ant-select-item-option-selected]:!bg-transparent [&.ant-select-item-option-active]:!bg-transparent"
                >
                  <div className="flex items-center w-full my-1 py-1 px-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 mx-1 my-1 rounded-lg gap-2 relative">
                    {getCurrentAssignees().some(a => a.key === member.id.toString()) && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className="relative px-3 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[12px] font-[600] shadow-sm">
                      {member.user_name ? member.user_name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-[600] text-gray-900 truncate">{member.user_name}</div>
                      <div className="text-xs text-gray-500 truncate">{member.email}</div>
                    </div>
                  </div>
                </Option>
              );
            })}
          </Select>
          
          <div className="flex items-center justify-between mt-3">
            <div className="text-sm text-gray-500">
              {getCurrentAssignees().length > 0 
                ? (
                    <span className="flex items-center">
                      <svg className="h-4 w-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {getCurrentAssignees().length} member{getCurrentAssignees().length !== 1 ? 's' : ''} selected
                    </span>
                  )
                : 'No members selected'}
            </div>
            {loading && (
              <div className="flex items-center text-sm text-blue-600">
                <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assignee;